// The task-group and task API (docs/12 "Task groups as built", "Execution as built") as the admin
// pages read it. `GET /api/task-groups?all=true` needs ViewAllTaskGroups; every group and task by
// id is readable with it too, while changes stay the owner's (403 for anyone else).
import { queryOptions } from "@tanstack/react-query";

import { api, type Schemas } from "../api/client";
import { requestJson } from "../api/http";
import { unwrap } from "../api/queries";
import { parseGroup, parseGroupRequirements, parseTask, parseTaskDetail, parseTimeline } from "./parse";

const groupPath = (id: string) => ({ params: { path: { task_group_id: id } } });
const taskPath = (id: string) => ({ params: { path: { task_id: id } } });

// ocs-api's list query (`TaskGroupListQuery`) lacks `parameter_in = Query`, so the spec puts
// `all` in the path and openapi-fetch would drop it; the URL is written here instead. The body
// is the spec's `TaskGroupDto[]`, unvalidated, exactly as the generated client would return it.
// Every user's groups for an admin (`all`), else the caller's own.
export const taskGroupsQuery = (all = true) =>
  queryOptions({
    queryKey: ["task-groups", all ? "all" : "own"],
    queryFn: async () => ((await requestJson("GET", all ? "/api/task-groups?all=true" : "/api/task-groups")) as Schemas["TaskGroupDto"][]).map(parseGroup),
  });

export const taskGroupQuery = (id: string) =>
  queryOptions({
    queryKey: ["task-groups", id],
    queryFn: async () => parseGroup(await unwrap(api.GET("/api/task-groups/{task_group_id}", groupPath(id)))),
  });

export const groupRequirementsQuery = (id: string) =>
  queryOptions({
    queryKey: ["task-groups", id, "requirements"],
    queryFn: async () => parseGroupRequirements(await unwrap(api.GET("/api/task-groups/{task_group_id}/requirements", groupPath(id)))),
  });

export const groupTasksQuery = (id: string) =>
  queryOptions({
    queryKey: ["task-groups", id, "tasks"],
    queryFn: async () => (await unwrap(api.GET("/api/task-groups/{task_group_id}/tasks", groupPath(id)))).map(parseTask),
  });

export const taskQuery = (id: string) =>
  queryOptions({
    queryKey: ["tasks", id],
    queryFn: async () => parseTaskDetail(await unwrap(api.GET("/api/tasks/{task_id}", taskPath(id)))),
  });

export const timelineQuery = (id: string) =>
  queryOptions({
    queryKey: ["tasks", id, "timeline"],
    queryFn: async () => parseTimeline(await unwrap(api.GET("/api/tasks/{task_id}/timeline", taskPath(id)))),
  });

/** The owner's own changes: cancel any unfinished task, retry a failed or cancelled one. */
export const cancelTask = (id: string) => unwrap(api.POST("/api/tasks/{task_id}/cancel", taskPath(id)));

export const retryTask = (id: string) => unwrap(api.POST("/api/tasks/{task_id}/retry", taskPath(id)));

/** Every step again from the start, visits forgotten (D86); the steps so far stay as history. */
export const startOverTask = (id: string) => unwrap(api.POST("/api/tasks/{task_id}/start-over", taskPath(id)));

/** Continue a finished task at a step it ran, the visits from that step's last run on undone (D89). */
export const moveBackTask = (id: string, nodeId: string) =>
  unwrap(api.POST("/api/tasks/{task_id}/move-back", { ...taskPath(id), body: { node_id: nodeId } }));

/**
 * Put a task under another of its group (D66), or detach it with null; 422 for another group's
 * task, the task itself or a cycle. Written by hand: the route arrives in the generated schema
 * with S56.
 */
export const setTaskParent = async (id: string, parentId: string | null) =>
  parseTask((await requestJson("PUT", `/api/tasks/${encodeURIComponent(id)}/parent`, { parent_task_id: parentId })) as Schemas["TaskDto"]);

/** The owner's own changes to a group (S112): pause and resume polling and new tasks; archive
 * stops it for good and cancels its queued tasks. */
export const pauseTaskGroup = (id: string) => unwrap(api.POST("/api/task-groups/{task_group_id}/pause", groupPath(id)));

export const resumeTaskGroup = (id: string) => unwrap(api.POST("/api/task-groups/{task_group_id}/resume", groupPath(id)));

/** "Refresh tasks now" (S128): poll the source at once; 409 when manual or not active, 429 within 10 s of the last poll. */
export const pollTaskGroup = async (id: string) => parseGroup(await unwrap(api.POST("/api/task-groups/{task_group_id}/poll", groupPath(id))));

export const archiveTaskGroup = (id: string) => unwrap(api.DELETE("/api/task-groups/{task_group_id}", groupPath(id)));
